import { useMemo, useState } from 'react';
import {
  EventBus, useGameStore, findFurniture, furnitureFootprint, FURNITURE_CATEGORIES, MAX_DESK_ITEMS,
} from '@campus/core';
import type { FurnitureCatalogEntry } from '@campus/core';
import { Button, Icon, Input, cx } from '../ds/index.js';
import { GridImage } from '../admin/FurniturePalette.js';

const PANEL_WIDTH = 340;
const THUMB_CELL = 24;

/**
 * DeskCustomizePanel — panneau du mode « Personnaliser » (son bureau).
 * Catalogue autorisé par l'admin, limité aux objets qui tiennent dans le
 * bureau, par catégorie. Toute la logique de pose est dans
 * game/systems/PersonalDesks.ts ; ce panneau n'émet que des intentions.
 */
export function DeskCustomizePanel() {
  const c = useGameStore((s) => s.personalDeskUi.customizing);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    if (!c) return [];
    const q = search.trim().toLowerCase();
    const entries = c.furnitureIds
      .map((id) => findFurniture(id))
      .filter((e): e is FurnitureCatalogEntry => !!e)
      .filter((e) => {
        const f = furnitureFootprint(e);
        return f.width <= c.deskWidth && f.height <= c.deskHeight && (!q || e.label.toLowerCase().includes(q));
      });
    const known = new Set<string>(FURNITURE_CATEGORIES.map((cat) => cat.id));
    return [
      ...FURNITURE_CATEGORIES.map((cat) => ({ id: cat.id, label: cat.label, items: entries.filter((e) => e.category === cat.id) })),
      { id: 'other', label: 'Autres', items: entries.filter((e) => !known.has(e.category)) },
    ].filter((g) => g.items.length > 0);
  }, [c?.furnitureIds, c?.deskWidth, c?.deskHeight, search]);

  if (!c) return null;

  const select = (id: string | null) => EventBus.emit('ui:personal-desk-select', { furnitureId: id });
  const full = c.count >= MAX_DESK_ITEMS;
  const help = c.carrying
    ? 'Clique sur ton bureau pour reposer l’objet. Échap : le remettre en place. Suppr : le retirer.'
    : c.selectedId
      ? 'Clique sur ton bureau pour poser l’objet (autant de fois que tu veux). Échap : ne plus rien tenir.'
      : 'Choisis un objet ci-dessous, puis clique sur ton bureau. Clique sur un objet posé pour le déplacer.';

  return (
    <aside
      aria-label="Personnaliser ton bureau"
      className="pl-pop"
      style={{
        position: 'absolute', top: 'calc(var(--pl-hud-top) + 16px)', right: 'calc(16px + var(--pl-side-panel, 0px))', bottom: 'calc(var(--pl-hud-bottom) + 16px)', width: PANEL_WIDTH,
        display: 'flex', flexDirection: 'column', pointerEvents: 'auto', zIndex: 'var(--z-hud)' as unknown as number, overflow: 'hidden',
      }}
    >
      <header style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <h2 style={{ margin: 0, font: '700 16px/1.25 var(--font-display)' }}>Personnaliser ton bureau</h2>
          <span style={{ fontSize: 12, color: full ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>{c.count} / {MAX_DESK_ITEMS} objets</span>
        </div>
        <p className="pl-help" style={{ margin: 0 }}>{help}</p>
        {c.carrying && (
          <Button variant="danger" size="sm" onClick={() => EventBus.emit('ui:personal-desk-remove-carried', {})}>Retirer l&apos;objet</Button>
        )}
        {c.notAllowed > 0 && (
          <div className="pl-alert" role="alert">
            {c.notAllowed > 1 ? `${c.notAllowed} objets (en rouge) ne sont plus autorisés` : 'Un objet (en rouge) n’est plus autorisé'} : retire-{c.notAllowed > 1 ? 'les' : 'le'} pour enregistrer.
          </div>
        )}
      </header>

      <div style={{ padding: '10px 16px 4px' }}>
        {/* Pendant la saisie, le clavier n'appartient plus au jeu (sinon ZQSD déplace l'avatar). */}
        <Input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un objet" aria-label="Rechercher un objet"
          onFocus={() => EventBus.emit('ui:modal-opened', {})}
          onBlur={() => EventBus.emit('ui:modal-closed', {})}
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 12px' }}>
        {groups.length === 0 && <p className="pl-help" style={{ padding: '8px 8px' }}>Aucun objet ne correspond.</p>}
        {groups.map((g) => {
          const isOpen = search.trim() !== '' || open.has(g.id);
          return (
            <div key={g.id}>
              <button
                type="button"
                className="pl-cat"
                aria-expanded={isOpen}
                onClick={() => setOpen((prev) => { const next = new Set(prev); if (next.has(g.id)) next.delete(g.id); else next.add(g.id); return next; })}
              >
                <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={14} />
                {g.label}
                <span className="pl-count">{g.items.length}</span>
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 6, padding: '4px 12px 10px' }}>
                  {g.items.map((entry) => {
                    const active = c.selectedId === entry.id;
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        aria-pressed={active}
                        aria-label={entry.label}
                        title={entry.label}
                        className={cx('pl-fthumb', active && 'pl-fthumb-on')}
                        onClick={() => select(active ? null : entry.id)}
                      >
                        <GridImage entry={entry} cell={THUMB_CELL} maxWidth={PANEL_WIDTH - 60} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <footer style={{ padding: 12, borderTop: '1px solid var(--color-border-default)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={() => EventBus.emit('ui:personal-desk-customize-cancel', {})} disabled={c.saving}>Annuler</Button>
        <Button variant="primary" onClick={() => EventBus.emit('ui:personal-desk-customize-save', {})} disabled={c.saving || c.notAllowed > 0}>
          {c.saving ? 'Enregistrement…' : 'Terminer'}
        </Button>
      </footer>
    </aside>
  );
}
